import React, { useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList, ComposedChart, Area, Line, ScatterChart, Scatter, ZAxis } from 'recharts';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import XMembers, { UpdateProfile } from '../components/XMembers';

import {
  getFirestore, doc, updateDoc
} from 'firebase/firestore';

const COLORS = [
  '#b54d7ac9', '#d1a054c9', '#409da0cb', '#a81d32cb', '#63a346c3', '#2a7296c6',
  '#d1a054c9', '#7b5ba1c9', '#46a38bc3', '#d16a54cb', '#54a0d1cb', '#8da346c3',
  '#a34692c3', '#46a35bc3', '#a37e46c3', '#4648a3c3', '#a34646c3', '#41b3a3c9',
  '#e27d60c9', '#8e8d8ac9', '#c2b9b0c9', '#7395aec9'
];

const AdminCheckReportz = ({ profiles, levels, onSendToParent }) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const today = new Date();
  const db = getFirestore();

  const handleApprove = async (user) => {
    try {
      await updateDoc(doc(db, "profiles", user.id), { member: true });
      onSendToParent(true)
    } catch (err) { console.error("Error fetching profiles:", err); }
  };

  const levelLookup = levels.reduce((acc, level) => {
    acc[level.id.toLowerCase().trim()] = {
      cost: Number(level.cost) || 0,
      lateFee: Number(level.lateFee) || 0,
      isFlagged: level.account_flag === true
    };
    return acc;
  }, {});

  const statusCats = {
    flagged: { name: 'Flagged', list: [] },
    incidents: { name: 'Incidents', list: [] },
    pending: { name: 'Pending', list: [] },
    expired: { name: 'Expired', list: [] },
    awaitingPayment: { name: 'Awaiting Payment', list: [] },
    active: { name: 'Active', list: [] },
  };

  const roleCats = {
    admins: { name: 'Admins', list: [] },
    board: { name: 'Board', list: [] },
    employees: { name: 'Employees', list: [] },
  };

  const approvalCats = {
    needsApproval: { name: 'Profiles not toggled as members', list: [] },
  };

  const financialBreakdown = {};
  const lostRevenueBreakdown = {};
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const membershipTypes = [...new Set(profiles
    .filter(p => !levelLookup[(p.type || "").toLowerCase().trim()]?.isFlagged)
    .map(p => p.type || "Unassigned")
  )];

  const monthlyData = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    const item = {
      month: monthNames[d.getMonth()],
      year: d.getFullYear().toString().slice(-2),
      fullLabel: `${monthNames[d.getMonth()]} '${d.getFullYear().toString().slice(-2)}`,
      totalRevenue: 0,
      memberCount: 0
    };
    membershipTypes.forEach(t => { item[t] = 0; });
    return item;
  });

  profiles.forEach(user => {
    const type = user.type || "Unassigned";
    const typeLower = type.toLowerCase().trim();
    const userLevel = levelLookup[typeLower] || { cost: 0, lateFee: 0, isFlagged: false };
    const fullName = user.name || user.displayName || "Unknown User";

    const userRef = { ...user, fullName };

    if (user.isAdmin) roleCats.admins.list.push(userRef);
    if (user.board) roleCats.board.list.push(userRef);
    if (typeLower === 'employee') roleCats.employees.list.push(userRef);

    if (user.member === false) {
      approvalCats.needsApproval.list.push(userRef);
    }

    let isExpired = false;
    if (user.expirationDate) {
      const expDate = user.expirationDate.toDate ? user.expirationDate.toDate() : new Date(user.expirationDate);
      if (expDate < today) isExpired = true;

      if (!userLevel.isFlagged && !user.hasIncident) {
        const monthDiff = (expDate.getFullYear() - today.getFullYear()) * 12 + (expDate.getMonth() - today.getMonth());
        if (monthDiff >= 0 && monthDiff < 12) {
          const amount = (userLevel.cost + userLevel.lateFee);
          monthlyData[monthDiff][type] += amount;
          monthlyData[monthDiff].totalRevenue += amount;
          monthlyData[monthDiff].memberCount += 1;
        }
      }
    }

    if (user.hasIncident) {
      statusCats.incidents.list.push(userRef);
    } else if (userLevel.isFlagged) {
      statusCats.flagged.list.push(userRef);
    } else if (user.member === false && user.membershipStatus === "active") {
      statusCats.pending.list.push(userRef);
    } else if (user.member === false && user.membershipStatus !== "active") {
      statusCats.awaitingPayment.list.push(userRef);
    } else {
      if (isExpired) {
        statusCats.expired.list.push(userRef);
      } else {
        statusCats.active.list.push(userRef);
      }
    }

    if (!userLevel.isFlagged && !user.hasIncident) {
      if (!financialBreakdown[type]) financialBreakdown[type] = { count: 0, revenue: 0 };
      financialBreakdown[type].count += 1;

      const isReturningMemberExpired = isExpired && user.member === true;
      const userValue = isReturningMemberExpired ? (userLevel.cost + userLevel.lateFee) : userLevel.cost;

      financialBreakdown[type].revenue += userValue;

      const isNewAwaitingPayment = user.member === false && user.membershipStatus !== "active";
      if (isExpired || isNewAwaitingPayment) {
        if (!lostRevenueBreakdown[type]) lostRevenueBreakdown[type] = { count: 0, loss: 0, isNew: false };

        lostRevenueBreakdown[type].count += 1;
        lostRevenueBreakdown[type].loss += userValue;
        if (isNewAwaitingPayment) lostRevenueBreakdown[type].isNew = true;
      }
    }
  });

  const totalRevenue = Object.values(financialBreakdown).reduce((sum, item) => sum + item.revenue, 0);
  const totalLoss = Object.values(lostRevenueBreakdown).reduce((sum, item) => sum + item.loss, 0);
  const netPotential = totalRevenue - totalLoss;

  const collectionRate = totalRevenue > 0 ? Math.round((netPotential / totalRevenue) * 100) : 0;
  const leadershipCount = new Set([...roleCats.admins.list.map(u => u.fullName), ...roleCats.board.list.map(u => u.fullName)]).size;

  const statusData = Object.values(statusCats).map(c => ({ name: c.name, value: c.list.length })).filter(i => i.value > 0);
  const roleData = Object.values(roleCats).map(c => ({ name: c.name, value: c.list.length })).filter(i => i.value > 0);

  const bubbleData = Object.entries(financialBreakdown)
    .map(([name, data], index) => ({
      name,
      revenue: data.revenue,
      members: data.count,
      avg: data.count > 0 ? (data.revenue / data.count).toFixed(2) : 0,
      z: data.count * 15,
      fill: COLORS[index % COLORS.length]
    }));

  const renderCustomLegend = (payload, justify = "justify-content-start", ps = "ps-4") => (
    <div className={`d-flex flex-wrap ${justify} ${ps} gap-2 mb-3`} style={{ fontSize: '10px', color: '#888' }}>
      {payload.map((entry, index) => (
        <div key={index} className="d-flex align-items-center">
          <div style={{ width: 10, height: 10, backgroundColor: entry.color || entry.fill, marginRight: 6 }} />
          {entry.value || entry.name}
        </div>
      ))}
    </div>
  );

  const customTooltipStyle = {
    backgroundColor: '#040404ea',
    border: '1px solid #444',
    borderRadius: '8px',
    padding: '12px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.5)'
  };

  return (
    <div className="admin-reports-container p-1 text-light">
      <style>{`
        @media print { .no-print { display: none !important; } .bg-black { background: white !important; border: 1px solid #eee !important; color: black !important; } }
        .pulse-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 10px; text-align: center; }
        .chart-container { background: #000; border: 1px solid #333; border-radius: 8px; padding: 10px; margin-bottom: 20px; }
        summary { cursor: pointer; outline: none; list-style: none; transition: all 0.2s ease; }
        summary::-webkit-details-marker { display: none; }
        .arrow-icon { display: inline-block; transition: transform 0.2s ease; font-size: 0.8rem; margin-right: 5px; }
        details[open] .arrow-icon { transform: rotate(90deg); }
      `}</style>

      {/* Pulse Row */}
      <div className="row g-2 mb-4 no-print">
        <div className="col-6">
          <div className="pulse-card border-info shadow-sm">
            <div className="text-info text-uppercase mb-1" style={{ fontSize: '0.6rem', fontWeight: 'bold' }}>Collection Rate</div>
            <div className="h4 mb-0 text-white fw-bold">{collectionRate}%</div>
            <div className="progress mt-2" style={{ height: '4px', background: '#333', marginBottom: '5px' }}>
              <div className="progress-bar bg-info" style={{ width: `${collectionRate}%` }}></div>
            </div>
          </div>
        </div>
        <div className="col-6">
          <div className="pulse-card border-secondary shadow-sm">
            <div className="text-secondary text-uppercase mb-1" style={{ fontSize: '0.6rem', fontWeight: 'bold' }}>Leadership Density</div>
            <div className="h4 mb-0 text-white fw-bold">{leadershipCount} <small style={{ fontSize: '0.7rem' }}>/ {profiles.length}</small></div>
            <div className="text-white-50 mt-1" style={{ fontSize: '0.55rem' }}>Total Admins + Board</div>
          </div>
        </div>
      </div>

      <div className="row g-0">
        <div className="col-12">
          <h6 className="text-center text-secondary text-uppercase " style={{ fontSize: '0.65rem' }}>Health</h6>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={statusData} cx="50%" cy="50%" innerRadius={40} outerRadius={55} dataKey="value" stroke="#fff" strokeWidth={0.5}>
                {statusData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip
                contentStyle={customTooltipStyle}
                formatter={(value, name, props) => {
                  const percent = ((value / profiles.length) * 100).toFixed(1);
                  return [
                    <span style={{ color: props.payload.fill }}>{value} Members ({percent}%)</span>,
                    <span style={{ color: '#fff' }}>{name} Status</span>
                  ];
                }}
              />
              <Legend wrapperStyle={{ marginBottom: "-25px" }} verticalAlign="bottom" content={(props) => renderCustomLegend(props.payload, "justify-content-center", "ps-0")} />
            </PieChart>
          </ResponsiveContainer>
          <hr></hr>

          <h6 className="text-center text-secondary text-uppercase" style={{ fontSize: '0.65rem' }}>Roles</h6>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={roleData} cx="50%" cy="50%" innerRadius={40} outerRadius={55} dataKey="value" stroke="#fff" strokeWidth={0.5}>
                {roleData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip
                contentStyle={customTooltipStyle}
                formatter={(value, name, props) => {
                  return [
                    <span style={{ color: props.payload.fill }}>{value} Profiles</span>,
                    <span style={{ color: '#fff' }}>{name} Role</span>
                  ];
                }}
              />
              <Legend wrapperStyle={{ marginBottom: "-25px" }} verticalAlign="bottom" content={(props) => renderCustomLegend(props.payload, "justify-content-center", "ps-0")} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      <hr></hr>

      <details
        className="mt-2 mb-4 no-print"
        onToggle={(e) => setIsCollapsed(!e.target.open)}
      >
        <summary className="text-left blue text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '1px', fontWeight: 'bold' }}>
          <span className="arrow-icon">▶</span>
          <span className="white">View Member</span> - Breakdowns
        </summary>
        <div className="text-light mt-3">

          {approvalCats.needsApproval.list.length > 0 && (
            <div className="mb-3 p-2 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderLeft: `4px solid #b276e3c9` }}>
              <h6 style={{ color: '#b276e3c9', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px' }} className="mb-1">
                {approvalCats.needsApproval.name} ({approvalCats.needsApproval.list.length})
              </h6>
              <div className="d-flex flex-wrap gap-2">
                {approvalCats.needsApproval.list.map((user, idx) => (
                  <Badge key={idx} bg="dark" className="rounded-1 border border-secondary text-secondary font-weight-normal px-2 py-1 d-flex align-items-center gap-2" style={{ fontSize: '0.65rem' }}>
                    <UpdateProfile theme="dark" idx={user.id} filex={user.filex} name={user.fullName} onSendToParent={() => onSendToParent(true)} hide={false} smallIcon={true} />
                    {user.fullName}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {[...Object.values(statusCats), ...Object.values(roleCats)].map((cat, i) => (
            cat.list.length > 0 && (
              <div key={i} className="mb-3 p-2 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderLeft: `4px solid ${COLORS[i % COLORS.length]}` }}>
                <h6 style={{ color: COLORS[i % COLORS.length], fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px' }} className="mb-1">
                  {cat.name} ({cat.list.length})
                </h6>
                <div className="d-flex flex-wrap gap-2">
                  {cat.list.map((user, idx) => (
                    <Badge key={idx} bg="dark" className="rounded-1 border border-secondary text-secondary font-weight-normal px-2 py-1 d-flex align-items-center gap-2" style={{ fontSize: '0.65rem' }}>
                      <UpdateProfile theme="dark" idx={user.id} filex={user.filex} name={user.fullName} onSendToParent={() => onSendToParent(true)} hide={false} smallIcon={true}/>
                      {user.fullName}
                      {/* APPROVE button moved specifically to Pending status list */}
                      {cat.name === 'Pending' && (
                        <span
                          onClick={() => handleApprove(user)}
                          className="text-success fw-bold"
                          style={{ cursor: 'pointer', borderLeft: '1px solid #444', paddingLeft: '6px' }}
                        >
                          APPROVE
                        </span>
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
            )
          ))}

        </div>
      </details>

      {/* Bubble Chart */}
      <div className="chart-container mt-3">
        <h6 className="text-center text-secondary text-uppercase mb-3" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>Tier Analysis (Value & Density)</h6>
        <ResponsiveContainer width="100%" height={250}>
          <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
            <XAxis type="number" dataKey="revenue" name="Revenue" unit="$" stroke="#7a7a7a" fontSize={9} />
            <YAxis type="number" dataKey="members" name="Members" stroke="#787878" fontSize={9} />
            <ZAxis type="number" dataKey="z" range={[50, 400]} />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              contentStyle={customTooltipStyle}
              formatter={(value, name, props) => {
                const color = props.payload.fill;
                const levelName = props.payload.name;
                const average = props.payload.avg;
                if (name === "Revenue") return [<span style={{ color }}>${value.toLocaleString()}</span>, <span style={{ color: '#fff' }}>Total Worth</span>];
                if (name === "Members") return [<span style={{ color }}>{value}</span>, <span style={{ color: '#fff' }}>Member Count</span>];
                return [
                  <div style={{ borderTop: '1px solid #333', marginTop: '5px', paddingTop: '5px' }}>
                    <span style={{ color }}>${average}</span> <small style={{ color: '#777' }}>avg/member</small>
                  </div>,
                  <span style={{ color: '#fff', fontWeight: 'bold' }}>{levelName}</span>
                ];
              }}
            />
            <Scatter name="Membership Levels" data={bubbleData}>
              {bubbleData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} stroke="#fff" strokeWidth={0.5} />)}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
        {renderCustomLegend(bubbleData, "justify-content-center", "ps-0")}
      </div>

      {/* Activity Trend */}
      <div className="chart-container">
        <h6 className="text-center text-secondary text-uppercase mb-3" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>Activity Trend (Volume vs. Value)</h6>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={monthlyData} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
            <XAxis dataKey="fullLabel" stroke="#777" fontSize={8} angle={-45} textAnchor="end" interval={0} />
            <YAxis yAxisId="left" stroke="#777" fontSize={8} tickFormatter={(v) => `$${v}`} />
            <YAxis yAxisId="right" orientation="right" stroke="#3296aaa2" fontSize={8} />
            <Tooltip
              contentStyle={customTooltipStyle}
              labelStyle={{ color: '#fff', fontWeight: 'bold', borderBottom: '1px solid #333', marginBottom: '8px', paddingBottom: '4px' }}
              formatter={(value, name) => {
                const color = name === 'totalRevenue' ? '#b54d7ac9' : '#3296aaa2';
                const label = name === 'totalRevenue' ? 'Revenue Value' : 'Member Count';
                const displayVal = name === 'totalRevenue' ? `$${value.toLocaleString()}` : `${value} Expirations`;
                return [<span style={{ color, fontWeight: 'bold' }}>{displayVal}</span>, <span style={{ color: '#fff' }}>{label}</span>];
              }}
            />
            <Area yAxisId="left" type="monotone" dataKey="totalRevenue" fill="rgba(181, 77, 122, 0.1)" stroke="#b54d7ac9" strokeWidth={2} />
            <Line yAxisId="right" type="step" dataKey="memberCount" stroke="#3296aaa2" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Stacked Revenue */}
      <div className="chart-container">
        <h6 className="text-center text-secondary text-uppercase mb-3" style={{ fontSize: '0.7rem' }}>12-Month Revenue Outlook</h6>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={monthlyData} margin={{ top: 20, right: 10, left: -25, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
            <XAxis dataKey="fullLabel" stroke="#777" fontSize={8} angle={-45} textAnchor="end" interval={0} />
            <YAxis stroke="#777" fontSize={8} tickFormatter={(v) => `$${v}`} />
            <Tooltip
              cursor={{ fill: 'rgba(255,255,255,0.05)' }}
              contentStyle={customTooltipStyle}
              labelStyle={{ color: '#fff', fontWeight: 'bold', marginBottom: '8px', borderBottom: '1px solid #333', paddingBottom: '4px' }}
              formatter={(value, name, props) => {
                return [<span style={{ color: props.color, fontWeight: 'bold' }}>${value.toLocaleString()}</span>, <span style={{ color: '#fff' }}>{name} Level</span>];
              }}
            />
            <Legend verticalAlign="top" content={(props) => renderCustomLegend(props.payload, "justify-content-start", "ps-4")} />
            {membershipTypes.map((type, index) => (
              <Bar key={type} dataKey={type} stackId="a" fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={0.3}>
                {index === membershipTypes.length - 1 && <LabelList dataKey="totalRevenue" position="top" formatter={(v) => v > 0 ? `$${v}` : ''} style={{ fill: '#ccc', fontSize: '9px', fontWeight: 'bold' }} />}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
        <div className="text-center text-white-50 mt-1" style={{ fontSize: '0.55rem', fontStyle: 'italic' }}>(Excludes flagged accounts and incidents)</div>
      </div>

      <hr className="border-secondary opacity-25 white" />

      {/* Financial Breakdown */}
      <div className="mt-3 p-3 chart-container" style={{ fontSize: '0.75rem' }}>
        <h6 className="text-white text-uppercase mb-2" style={{ fontSize: '0.7rem', letterSpacing: '1px' }} title="Base Cost + Late Fee for Expired Members | Base Cost only for New Users">Potential Revenue Breakdown ⓘ</h6>
        {Object.entries(financialBreakdown).sort().map(([t, d]) => (
          <div key={t} className="col-12 d-flex justify-content-between border-bottom border-secondary pb-1 mb-1">
            <span className="text-white-50">{d.count} {t}</span>
            <span className="text-success fw-bold">${d.revenue.toLocaleString()}</span>
          </div>
        ))}
        <div className="col-12 d-flex justify-content-between pt-2 mt-1 rounded bg-white bg-opacity-10 p-2 text-white fw-bold">
          <span>Total Potential</span>
          <span className="text-success fw-bold">${totalRevenue.toLocaleString()}</span>
        </div>
      </div>

      {/* Losses/New */}
      <div className="mt-3 p-3 chart-container " style={{ fontSize: '0.75rem' }}>
        <h6 className="text-danger text-uppercase mb-2" style={{ fontSize: '0.7rem', fontWeight: 'bold', letterSpacing: '1px' }} title="Reflects the exact potential value lost (includes late fees for existing members only)">Expired Losses or New Users that have not paid yet ⓘ</h6>
        {Object.entries(lostRevenueBreakdown).sort().map(([t, d]) => (
          <div key={t} className="col-12 d-flex justify-content-between border-bottom border-secondary pb-1 mb-1 text-white">
            <span>{d.count} {t} ({d.isNew ? 'New' : 'Expired'})</span>
            <span className="text-danger-subtle fw-bold">-${d.loss.toLocaleString()}</span>
          </div>
        ))}
        <div className="col-12 d-flex justify-content-between pt-2 mt-1 rounded bg-danger bg-opacity-10 p-2 text-white fw-bold shadow-sm">
          <span>Net Loss</span>
          <span className="text-danger fw-bold">-${totalLoss.toLocaleString()}</span>
        </div>
      </div>

      {/* Net Expected */}
      <div className="mt-3 p-3 rounded bg-info bg-opacity-10 border border-info mb-4 shadow-sm" style={{ fontSize: '0.8rem' }}>
        <div className="d-flex justify-content-between align-items-center text-info text-uppercase fw-bold">
          <span style={{ letterSpacing: '0.5px' }}>Net Expected Revenue</span>
          <span style={{ fontSize: '1.1rem' }} className="text-info fw-bold">${netPotential.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};

export default AdminCheckReportz;